/**
 * @file Touch-target component for optimized mobile interactions
 * @description 
 * This component provides a wrapper that ensures interactive elements
 * have sufficient touch target sizes for mobile accessibility.
 * It follows WCAG guidelines that recommend touch targets be at least 44x44px.
 * 
 * Key features:
 * - Ensures minimum touch target size of 44x44px
 * - Preserves original component styling and behavior
 * - Supports all HTML attributes and event handlers
 * - Provides visual feedback for touch interactions
 * - Can be used with any interactive element
 * 
 * @dependencies
 * - React: For component rendering
 * - cn: Utility for merging class names
 */

import * as React from "react";
import { cn } from "@/lib/utils";

export interface TouchTargetProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Whether to add visual feedback on touch/active state
   * @default true
   */
  withFeedback?: boolean;
  
  /**
   * Minimum size of the touch target area in pixels
   * @default 44
   */
  minSize?: number;
  
  /**
   * Whether to center the child content
   * @default true
   */
  centered?: boolean;
  
  /**
   * Whether to make the touch target fill its container
   * @default false
   */
  fullWidth?: boolean;
}

/**
 * TouchTarget component that ensures interactive elements have
 * sufficient size for comfortable touch interaction on mobile devices.
 * 
 * @example
 * <TouchTarget>
 *   <button>Click me</button>
 * </TouchTarget>
 */
const TouchTarget = React.forwardRef<HTMLDivElement, TouchTargetProps>(
  ({ 
    className, 
    children, 
    withFeedback = true, 
    minSize = 44, 
    centered = true, 
    fullWidth = false,
    ...props 
  }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          // Base styles ensuring minimum touch target size
          `inline-flex ${fullWidth ? 'w-full' : ''} touch-target`,
          // Centering content if requested
          centered ? "items-center justify-center" : "",
          // Add touch feedback styles if requested
          withFeedback ? "touch-active" : "",
          className
        )}
        style={{ 
          minHeight: `${minSize}px`, 
          minWidth: `${minSize}px`,
          ...props.style 
        }}
        {...props}
      >
        {children}
      </div>
    );
  }
);

TouchTarget.displayName = "TouchTarget";

export { TouchTarget };

export default TouchTarget;